<template>
    <div class="loading">
        <div class="loading__backdrop"></div>
        <div class="loading__container">
            <div class="loading__content">
                <!-- Main Spinner -->
                <div class="loading__spinner">
                    <div class="loading__outer-ring">
                        <div class="loading__gradient-ring"></div>
                    </div>
                    <div class="loading__middle-ring">
                        <div class="loading__pulse-ring"></div>
                    </div>
                    <div class="loading__inner-core">
                        <div class="loading__core-dot"></div>
                        <div class="loading__core-pulse"></div>
                    </div>
                </div>
                
                <!-- Loading Text with typing effect -->
                <div class="loading__text">
                    <span class="loading__title">{{ displayText }}</span>
                    <span class="loading__cursor">|</span>
                </div>
                
                <!-- Progress dots -->
                <div class="loading__progress">
                    <div v-for="i in 4" :key="i" class="loading__dot" :class="{ active: activeDot >= i }"></div>
                </div>
            </div>
            
            <!-- Decorative elements -->
            <div class="loading__decorations">
                <div class="decoration decoration--1"></div>
                <div class="decoration decoration--2"></div>
                <div class="decoration decoration--3"></div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "LoadingSpinner",
    data() {
        return {
            texts: ['Yükleniyor', 'Loading', 'Cargando', 'Chargement'],
            currentTextIndex: 0,
            displayText: '',
            textIndex: 0,
            isDeleting: false,
            activeDot: 1
        };
    },
    mounted() {
        this.typeText();
        this.animateDots();
    },
    methods: {
        typeText() {
            const currentText = this.texts[this.currentTextIndex];
            
            if (this.isDeleting) {
                this.displayText = currentText.substring(0, this.textIndex - 1);
                this.textIndex--;
            } else {
                this.displayText = currentText.substring(0, this.textIndex + 1);
                this.textIndex++;
            }

            let typeSpeed = this.isDeleting ? 50 : 100;

            if (!this.isDeleting && this.textIndex === currentText.length) {
                typeSpeed = 2000;
                this.isDeleting = true;
            } else if (this.isDeleting && this.textIndex === 0) {
                this.isDeleting = false;
                this.currentTextIndex = (this.currentTextIndex + 1) % this.texts.length;
                typeSpeed = 500;
            }

            setTimeout(() => this.typeText(), typeSpeed);
        },
        
        animateDots() {
            setInterval(() => {
                this.activeDot = this.activeDot >= 4 ? 1 : this.activeDot + 1;
            }, 500);
        }
    }
};
</script>

<style scoped>
.loading {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999999;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    overflow: hidden;
}

.loading__backdrop {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, 
        rgba(99, 102, 241, 0.02) 0%, 
        rgba(139, 92, 246, 0.03) 50%, 
        rgba(16, 185, 129, 0.02) 100%);
    backdrop-filter: blur(30px) saturate(120%);
    -webkit-backdrop-filter: blur(30px) saturate(120%);
}

.loading__container {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 3rem;
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.95) 0%,
        rgba(255, 255, 255, 0.85) 100%);
    backdrop-filter: blur(50px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 2rem;
    box-shadow: 
        0 32px 64px rgba(0, 0, 0, 0.12),
        0 0 0 1px rgba(255, 255, 255, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.3);
    animation: containerPulse 4s ease-in-out infinite;
    min-width: 280px;
    text-align: center;
    position: relative;
    overflow: hidden;
}

@keyframes containerPulse {
    0%, 100% {
        transform: scale(1);
        box-shadow: 
            0 32px 64px rgba(0, 0, 0, 0.12),
            0 0 0 1px rgba(255, 255, 255, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.3);
    }
    50% {
        transform: scale(1.02);
        box-shadow: 
            0 40px 80px rgba(0, 0, 0, 0.16),
            0 0 0 1px rgba(255, 255, 255, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.4);
    }
}

.loading__content {
    position: relative;
    z-index: 2;
}

.loading__spinner {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading__outer-ring {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: conic-gradient(from 0deg, transparent, var(--primary), var(--secondary), transparent);
    animation: spin 3s linear infinite;
    padding: 4px;
}

.loading__gradient-ring {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.9) 0%,
        rgba(255, 255, 255, 0.7) 100%);
}

.loading__middle-ring {
    position: absolute;
    width: 80%;
    height: 80%;
    border-radius: 50%;
    border: 2px solid transparent;
    background: linear-gradient(45deg, var(--secondary), var(--accent)) border-box;
    animation: spinReverse 2s linear infinite;
}

.loading__pulse-ring {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: radial-gradient(circle, 
        rgba(255, 255, 255, 0.8) 0%,
        rgba(255, 255, 255, 0.4) 70%,
        transparent 100%);
    animation: pulse 2s ease-in-out infinite;
}

.loading__inner-core {
    position: absolute;
    width: 40%;
    height: 40%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading__core-dot {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: linear-gradient(45deg, var(--primary), var(--secondary));
    box-shadow: 0 0 20px rgba(99, 102, 241, 0.5);
    animation: corePulse 1.5s ease-in-out infinite;
}

.loading__core-pulse {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: radial-gradient(circle, 
        rgba(99, 102, 241, 0.3) 0%,
        transparent 70%);
    animation: expand 2s ease-out infinite;
}

.loading__text {
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-height: 2rem;
}

.loading__title {
    font-size: 1.25rem;
    font-weight: 600;
    background: linear-gradient(135deg, var(--primary), var(--secondary));
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.05em;
}

.loading__cursor {
    font-size: 1.25rem;
    font-weight: 300;
    color: var(--primary);
    animation: blink 1s infinite;
}

.loading__progress {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    justify-content: center;
}

.loading__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba(99, 102, 241, 0.3);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    transform: scale(0.8);
}

.loading__dot.active {
    background: linear-gradient(45deg, var(--primary), var(--secondary));
    transform: scale(1.2);
    box-shadow: 0 0 15px rgba(99, 102, 241, 0.6);
}

.loading__decorations {
    position: absolute;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

.decoration {
    position: absolute;
    border-radius: 50%;
    background: linear-gradient(45deg, 
        rgba(99, 102, 241, 0.1), 
        rgba(139, 92, 246, 0.1));
}

.decoration--1 {
    width: 20px;
    height: 20px;
    top: 20%;
    right: 15%;
    animation: float 4s ease-in-out infinite;
}

.decoration--2 {
    width: 16px;
    height: 16px;
    bottom: 25%;
    left: 20%;
    animation: float 3s ease-in-out infinite reverse;
}

.decoration--3 {
    width: 12px;
    height: 12px;
    top: 60%;
    right: 20%;
    animation: float 5s ease-in-out infinite;
}

@keyframes float {
    0%, 100% {
        transform: translateY(0px) rotate(0deg);
        opacity: 0.6;
    }
    50% {
        transform: translateY(-10px) rotate(180deg);
        opacity: 1;
    }
}

/* Animations */
@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

@keyframes spinReverse {
    from { transform: rotate(360deg); }
    to { transform: rotate(0deg); }
}

@keyframes pulse {
    0%, 100% {
        opacity: 0.6;
        transform: scale(1);
    }
    50% {
        opacity: 1;
        transform: scale(1.1);
    }
}

@keyframes corePulse {
    0%, 100% {
        transform: scale(1);
        box-shadow: 0 0 20px rgba(99, 102, 241, 0.5);
    }
    50% {
        transform: scale(1.2);
        box-shadow: 0 0 30px rgba(99, 102, 241, 0.8);
    }
}

@keyframes expand {
    0% {
        transform: scale(0);
        opacity: 1;
    }
    100% {
        transform: scale(2);
        opacity: 0;
    }
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}

/* Responsive Design */
@media (max-width: 768px) {
    .loading__container {
        padding: 3rem 2rem;
        min-width: 240px;
        margin: 0 1rem;
        border-radius: 1.5rem;
    }
    
    .loading__spinner {
        width: 100px;
        height: 100px;
        margin-bottom: 1.5rem;
    }
    
    .loading__title {
        font-size: 1.1rem;
    }
    
    .loading__dot {
        width: 6px;
        height: 6px;
    }
}

@media (max-width: 480px) {
    .loading__container {
        padding: 2rem 1.5rem;
        min-width: 200px;
        border-radius: 1rem;
    }
    
    .loading__spinner {
        width: 80px;
        height: 80px;
        margin-bottom: 1rem;
    }
    
    .loading__title {
        font-size: 1rem;
    }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
    .loading__backdrop {
        background: linear-gradient(135deg, 
            rgba(15, 23, 42, 0.95) 0%, 
            rgba(30, 41, 59, 0.97) 100%);
    }
    
    .loading__container {
        background: linear-gradient(145deg, 
            rgba(31, 41, 55, 0.95) 0%,
            rgba(17, 24, 39, 0.9) 100%);
        border: 1px solid rgba(55, 65, 81, 0.3);
        box-shadow: 
            0 32px 64px rgba(0, 0, 0, 0.4),
            0 0 0 1px rgba(55, 65, 81, 0.3),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }
    
    .loading__gradient-ring {
        background: linear-gradient(145deg, 
            rgba(31, 41, 55, 0.9) 0%,
            rgba(17, 24, 39, 0.8) 100%);
    }
}

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .loading__outer-ring,
    .loading__middle-ring,
    .loading__core-dot,
    .loading__core-pulse,
    .loading__pulse-ring,
    .decoration {
        animation: none;
    }
    
    .loading__container {
        animation: none;
    }
    
    .loading__cursor {
        animation: none;
        opacity: 1;
    }
}

/* High Performance Mode */
.loading__outer-ring,
.loading__middle-ring,
.loading__core-dot,
.loading__core-pulse {
    will-change: transform;
    transform: translateZ(0);
}

.loading__container {
    will-change: transform, opacity;
    transform: translateZ(0);
}
</style>